import React, {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {ProfileMain} from 'src/components/profile-tab-structure/profile-main';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {McpLimitAuditLog} from './audit-log/mcp-limit-audit-log';
import {mcpLimitLogDataActions} from './audit-log/slices/mcp-limit-log-data.slice';
import {McpLimitsForm} from './mcp-limits-form';
import {useAccountConnectorLimits} from './useAccountConnectorLimits';

type Props = {
    accountId: number,
};

export const AccountConnectorLimitsTab = ({accountId}: Props) => {
    const dispatch = useDispatch();

    const onSaveSuccess = useCallback(() => {
        setTimeout(() => dispatch(mcpLimitLogDataActions.requireRefresh()), 1000);
    }, [dispatch]);

    const {
        busy,
        editDisabled,
        isPremiumMcpEnabled,
        mcpLimitsDictionary,
        data,
        tempLimitsSettings,
        anyChanged,
        isLoadingAny,
        combinedError,
        today,
        isDefaultLimits,
        getDefaultValue,
        onSave,
        onDiscard,
        onLimitChange,
        onResetToDefaults,
        changeTemporaryLimit,
        clearTemporaryLimit,
        clearCustomLimit,
        changeTemporaryLimitMode,
        onExpiresOnChange,
    } = useAccountConnectorLimits(accountId, {onSaveSuccess});

    return (
        <ProfileMain
            header={(
                <Item grow={1}>
                    <Group justify="space-between">
                        <Typography
                            fontWeight="semi-bold"
                            variant="heading-level-3">
                            Connector Limits
                        </Typography>
                        {!editDisabled && (
                            <Group>
                                <Button
                                    variant="Type.SECONDARY"
                                    disabled={!anyChanged || isLoadingAny}
                                    onClick={onDiscard}>
                                    Discard Changes
                                </Button>
                                <Button
                                    busy={busy}
                                    variant="Type.PRIMARY"
                                    disabled={!anyChanged || isLoadingAny}
                                    onClick={onSave}>
                                    Save Changes
                                </Button>
                            </Group>
                        )}
                    </Group>
                </Item>
            )}>
            <Group direction="v" gap="s">
                <McpLimitsForm
                    dictionary={mcpLimitsDictionary}
                    isPremiumMcpEnabled={isPremiumMcpEnabled}
                    data={data}
                    getDefaultValue={getDefaultValue}
                    onLimitChange={onLimitChange}
                    editDisabled={editDisabled}
                    isLoading={isLoadingAny}
                    error={combinedError}
                    tempLimitsSettings={tempLimitsSettings}
                    today={today}
                    changeTemporaryLimit={changeTemporaryLimit}
                    clearTemporaryLimit={clearTemporaryLimit}
                    clearCustomLimit={clearCustomLimit}
                    changeTemporaryLimitMode={changeTemporaryLimitMode}
                    onExpiresOnChange={onExpiresOnChange}
                    isDefaultLimits={isDefaultLimits}
                    onResetToDefaults={onResetToDefaults}
                    tooltipText="This value sets the standard limit for tool calls per month for all the users in the account."
                />
                <McpLimitAuditLog accountId={accountId}/>
            </Group>
        </ProfileMain>
    );
};
